import type { ReactNode } from 'react';

import type { ToolUseItem } from '../../../contracts/harness.types';
import {
  isReportIssueToolName,
  REPORT_ISSUE_APPROVAL_KIND,
  type ReportIssueType,
} from '../../../contracts/report-issue.types';
import type { ResolveDraftApproval } from '../../inline-approval/draft-approval-block';
import { ReportIssueApprovalBlock } from '../../inline-approval/report-issue-approval-block';

/** Once-per-approval `requested` analytics guard (same idiom as computer use). */
const reportIssueRequestedIds = new Set<string>();

export type ReportIssueApprovalTrackProps = {
  action: 'requested' | 'resolved';
  object_type: 'issue_report';
  issue_type: ReportIssueType;
  project_id: string;
  agent_run_id: string;
  surface: 'project_chat';
  outcome?: 'approved' | 'denied';
  description_edited?: boolean;
};

export const renderReportIssueApproval = ({
  item,
  pendingApprovalId,
  toolInput,
  projectId,
  agentRunId,
  onResolve,
  onTrack,
}: {
  item: ToolUseItem;
  pendingApprovalId: string;
  toolInput: Record<string, unknown>;
  projectId: string;
  agentRunId: string;
  onResolve?: ResolveDraftApproval;
  /** App analytics port. `requested` fires once per approval id. */
  onTrack?: (props: ReportIssueApprovalTrackProps) => void;
}): ReactNode | null => {
  if (
    item.approval?.approval_kind !== REPORT_ISSUE_APPROVAL_KIND &&
    !isReportIssueToolName(item.name)
  ) {
    return null;
  }
  const issueType: ReportIssueType = toolInput.issue_type === 'safety' ? 'safety' : 'bug';
  if (!reportIssueRequestedIds.has(pendingApprovalId)) {
    reportIssueRequestedIds.add(pendingApprovalId);
    onTrack?.({
      action: 'requested',
      object_type: 'issue_report',
      issue_type: issueType,
      project_id: projectId,
      agent_run_id: agentRunId,
      surface: 'project_chat',
    });
  }
  return (
    <ReportIssueApprovalBlock
      toolInput={toolInput}
      approvalId={pendingApprovalId}
      onResolve={onResolve}
      onResolved={(resolvedType, outcome, extra) => {
        onTrack?.({
          action: 'resolved',
          outcome: outcome === 'granted' ? 'approved' : 'denied',
          object_type: 'issue_report',
          issue_type: resolvedType,
          description_edited: extra.description_edited,
          project_id: projectId,
          agent_run_id: agentRunId,
          surface: 'project_chat',
        });
      }}
    />
  );
};
